import { defineStore } from "pinia";
import { ref } from "vue";
import { defaultCalcCostDetailData, } from "@erp/shared/components/ui/Document/CalcCost/const";
import { getObtainConfirmDetail, } from "@erp/shared/api/generated/wrappers/obtain";
import { defaultCheckBoxItem, } from "@erp/shared/components/ui/Document/VersionCombobox/types";
import { APPROVAL_PHASE } from "@erp/shared/components/ui/Approval/approval-phase.types";
import { getObtainHistoryVersionData } from "@erp/shared/api/generated/wrappers/obtain";
import { getObtainHistoryVersions } from "@erp/shared/api/generated/wrappers/obtain";
/**
 * @description 문서 관련 상태 관리
 *
 * @member isDocumentDialogOpen - 문서 다이얼로그 열림 여부
 * @member selectedData - 선택된 문서 데이터
 *
 * @member basicInfo - 문서 기본 정보
 * @member laborList - 인건비 목록
 * @member bomList - BOM 목록
 * @member writerInfo - 작성자 정보
 * @member calcData - 산출내역서 데이터
 *
 * @member versions - 문서 버전 목록
 * @member version - 현재 선택된 문서 버전
 * @member documentMap - 문서 데이터 맵 (버전별 데이터 캐싱용)
 *
 * @method setVersion - 선택된 버전 설정
 * @method getDocumentData - 문서 데이터를 API로부터 받아와 설정하는 함수.
 * @method setDocumentData - 문서 데이터를 설정
 */
export const useDocument = defineStore("obtainSearch-useDocument", () => {
    const isDocumentDialogOpen = ref(false);
    const selectedData = ref(null);
    const basicInfo = ref(null);
    const laborList = ref([]);
    const bomList = ref([]);
    const writerInfo = ref({
        name: "",
        officePhoneNumber: "",
    });
    const calcData = ref(defaultCalcCostDetailData);
    const versions = ref([]);
    const version = ref(defaultCheckBoxItem);
    const documentMap = ref(new Map());
    const setVersion = async (newVersionValue) => {
        if (newVersionValue < 0) {
            throw new Error("[버전 설정에 실패했습니다] 버전번호: " + newVersionValue);
        }
        const targetVersion = versions.value.find((v) => v.value === newVersionValue) ||
            defaultCheckBoxItem;
        version.value = targetVersion;
        if (documentMap.value.has(newVersionValue)) {
            const data = documentMap.value.get(newVersionValue);
            if (!data)
                return;
            setDocumentData(data);
            return;
        }
        if (!targetVersion.historyId) {
            console.warn("No historyId found for version:", targetVersion);
            return;
        }
        const newVersionData = await getObtainHistoryVersionData(targetVersion.historyId);
        // 빠른 연속 전환 방어: await 후 현재 버전이 바뀌었으면 스킵
        if (version.value.value !== newVersionValue)
            return;
        documentMap.value.set(newVersionValue, {
            info: newVersionData.info,
            calc: newVersionData.calc,
            labor: newVersionData.labor,
            bom: newVersionData.bom,
        });
        setDocumentData(newVersionData);
    };
    const setDocumentData = (data) => {
        selectedData.value = data;
        basicInfo.value = data.info;
        calcData.value = data.calc;
        writerInfo.value = {
            name: data.info.createrName ?? "",
            officePhoneNumber: data.info.createrPhoneNumber ?? "",
        };
        laborList.value = data.labor ? [...data.labor] : [];
        bomList.value = data.bom?.bomJson ? [...data.bom.bomJson] : [];
    };
    const initDocumentData = () => {
        selectedData.value = null;
        basicInfo.value = null;
        calcData.value = defaultCalcCostDetailData;
        writerInfo.value = {
            name: "",
            officePhoneNumber: "",
        };
        laborList.value = [];
        bomList.value = [];
        versions.value = [];
        version.value = defaultCheckBoxItem;
        documentMap.value.clear();
    };
    const getDocumentData = async (id) => {
        initDocumentData();
        const res = await getObtainConfirmDetail(id);
        version.value = {
            text: res.info.version
                ? `v${res.info.version} (${res.info.approvalPhase})`
                : "(버전 없음)",
            approvalPhase: res.info.approvalPhase || APPROVAL_PHASE.잘못된승인단계,
            value: res.info.version || 0,
        };
        versions.value.push(version.value);
        documentMap.value.set(version.value.value, {
            info: res.info,
            calc: res.calc,
            labor: res.labor,
            bom: res.bom,
        });
        setDocumentData(res);
        const histories = await getObtainHistoryVersions(res.info.id);
        histories.map((history) => {
            if (Number(history.version) === Number(res.info.version))
                return;
            versions.value.push({
                historyId: history.id,
                text: history.version
                    ? `v${history.version} (${history.approvalPhase})`
                    : "(버전 없음)",
                approvalPhase: history.approvalPhase || APPROVAL_PHASE.잘못된승인단계,
                value: history.version,
            });
        });
        isDocumentDialogOpen.value = true;
    };
    return {
        isDocumentDialogOpen,
        selectedData,
        basicInfo,
        laborList,
        calcData,
        writerInfo,
        bomList,
        versions,
        version,
        setVersion,
        getDocumentData,
    };
});
